import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Pagination,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { slotsService } from './slots.service';
import { Slot } from './slots.dto';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '@/helpers';
import { formatTime } from '@/helpers';

const ViewSlot = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { token, isAuthenticated, loading: authLoading, logout } = useAuth();

  // State management
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSlots, setTotalSlots] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<number | null>(null);
  const itemsPerPage = 10;

  // Set up logout callback for session expired
  useEffect(() => {
    slotsService.setLogoutCallback(logout);
  }, [logout]);

  // Fetch slots by service ID from API
  const fetchSlots = useCallback(async () => {
    if (!token || !isAuthenticated || !id) {
      setLoading(false);
      return;
    }

    console.log('🔄 Fetching slots for service ID:', id, 'Page:', currentPage);

    try {
      setLoading(true);
      setError(null);

      const response = await slotsService.getSlotListByServiceId(
        token,
        parseInt(id),
        {
          page: currentPage,
          limit: itemsPerPage,
        }
      );

      console.log('Slots data received:', response);
      setSlots(response.data || []);
      setTotalPages(response.meta?.lastPage || 1);
      setTotalSlots(response.meta?.total || 0);
    } catch (err) {
      console.error('Error fetching slots:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch slots');
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, id, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // Handlers
  const handleBack = () => {
    navigate('/slots');
  };

  const handleDeleteClick = (slotId: number) => {
    setSlotToDelete(slotId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !slotToDelete) return;

    try {
      await slotsService.deleteSlot(token, slotToDelete);
      showSnackbar('Slot deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setSlotToDelete(null);

      // If current page becomes empty after delete, go to previous page
      if (slots.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchSlots();
      }
    } catch (err) {
      console.error('Error deleting slot:', err);
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to delete slot',
        'error'
      );
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSlotToDelete(null);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    console.log(
      '📄 Pagination clicked - Changing page from',
      currentPage,
      'to',
      page
    );
    setCurrentPage(page);
  };

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' = 'success'
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Slots
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  function getDuration(timeInterval: number) {
    throw new Error('Function not implemented.');
  }

  // Render
  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Slots for Service</Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Slots
        </Button>
      </Box>

      {slots.length === 0 ? (
        <Alert severity="info">No slots found for this service.</Alert>
      ) : (
        <>
          {/* Slots Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Start Date</TableCell>
                    {/* <TableCell>Day</TableCell> */}
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    {/* <TableCell>Time Interval</TableCell> */}
                    {/* <TableCell>Bookings Per Slot</TableCell> */}
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {slots.map((slot, index) => (
                    <TableRow key={slot.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(slot?.date || 'N/A')}
                        </Typography>
                      </TableCell>
                      {/* <TableCell>
                        <Typography variant="body2">
                          {slot.dayOfWeek || 'N/A'}
                        </Typography>
                      </TableCell> */}
                      <TableCell>
                        <Typography variant="body2">
                          {formatTime(slot.startTime)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTime(slot.endTime)}
                        </Typography>
                      </TableCell>
                      {/* <TableCell>
                        <Typography variant="body2">
                          {slot?.bookingPerSlot}
                        </Typography>
                      </TableCell> */}
                      <TableCell>
                        <Chip
                          label={slot.status === 1 ? 'Active' : 'Inactive'}
                          color={slot.status === 1 ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(slot.id!)}
                          size="small"
                          title="Delete Slot"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Pagination */}
          {!loading && totalPages > 1 ? (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" mt={3}>
              <Typography variant="caption" color="text.secondary">
                {loading
                  ? 'Loading...'
                  : `Total: ${totalSlots} slot${totalSlots !== 1 ? 's' : ''} - Showing page ${currentPage} of ${totalPages}`}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this slot? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewSlot;
